import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Skeleton, Spin } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useNotification } from 'app/providers'
import classNames from 'classnames'
import { Button } from 'common/components/Button/Button'
import { CameraIcon, UploadIcon } from 'common/components/Icons'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import { useAppDispatch } from 'common/hooks/redux'
import { DateService } from 'common/services/dateService'
import type { EncounterPhotoApiItem } from 'features/Procedure/state/procedure-api.type'
import {
  procedureApi,
  useFetchEncounterPhotosQuery,
  useUpdateEncounterPhotosMutation,
} from 'features/Procedure/state/procedure.api'
import { ProcedureStoreTagTypes } from 'features/Procedure/state/procedure.type'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { PhotoPreviewModal } from './PhotoPreviewModal'
import {
  createEncounterPhotoUploadFailureError,
  getEncounterPhotoErrorDescription,
  getEncounterPhotoSelectionReadErrorMessage,
  retryEncounterPhotoUpdates,
  snapshotEncounterPhotoFile,
  showPersistentPhotoError,
  type EncounterPhotoUploadFailure,
} from './photos.helpers'
import {
  addPhotoActionBreadcrumb,
  capturePhotoActionError,
  capturePhotoActionMessage,
  addPhotoUploadBreadcrumb,
  capturePhotoUploadError,
  capturePhotoUploadWarning,
} from './photos.sentry'
import styles from './photos.module.scss'
import type { PhotoData } from './photos.types'
import {
  parseCategoryFromFileName,
  parsePositionFromFileName,
  updatePositionInFileName,
} from './photos.utils'
import { ProcedurePictureModal } from './ProcedurePictureModal'
import { useEncounterPhotoUpload } from './useEncounterPhotoUpload'

type PhotoCategory = 'before' | 'after'
type PhotoNoteAutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'
type CategoryUploadState = {
  isUploading: boolean
  fileCount: number
}
type ModalUploadState = {
  isUploading: boolean
  fileCount: number
  category: PhotoCategory | null
}
type ModalUploadTaskContext = {
  category: PhotoCategory | null
  fileCount: number
}

const photoCategories: PhotoCategory[] = ['before', 'after']
const PHOTO_NOTE_AUTOSAVE_DELAY_MS = 2000

const uploadLabels: Record<PhotoCategory, string> = {
  before: 'Take Before Pictures',
  after: 'Take After Pictures',
}

const galleryUploadLabels: Record<PhotoCategory, string> = {
  before: 'Upload before pictures from gallery',
  after: 'Upload after pictures from gallery',
}

const columnLabels: Record<PhotoCategory, string> = {
  before: 'Before',
  after: 'After',
}

const createEmptyPhotoColumns = (): Record<PhotoCategory, PhotoData[]> => ({
  before: [],
  after: [],
})

const isPhotoCategory = (value: unknown): value is PhotoCategory =>
  value === 'before' || value === 'after'

const mapApiPhotosToColumns = (
  items: EncounterPhotoApiItem[],
): Record<PhotoCategory, PhotoData[]> => {
  const itemsWithPosition = items.map((item) => {
    let category: PhotoCategory = 'before'
    if (isPhotoCategory(item.category)) {
      category = item.category
    } else if (item.filename) {
      category = parseCategoryFromFileName(item.filename)
    }

    const position = parsePositionFromFileName(item.filename)

    return {
      id: item.id.toString(),
      imageUrl: item.thumbnail || item.data || item.url || '',
      label: item.filename || `${columnLabels[category]} ${columnLabels[category].length + 1}`,
      note: item.note ?? undefined,
      category,
      position,
    }
  })

  const result = createEmptyPhotoColumns()

  itemsWithPosition.forEach((item) => {
    result[item.category].push({
      id: item.id,
      imageUrl: item.imageUrl,
      label: item.label,
      note: item.note,
    })
  })

  result.before.sort((a, b) => {
    const posA = parsePositionFromFileName(a.label)
    const posB = parsePositionFromFileName(b.label)
    return posA - posB
  })

  result.after.sort((a, b) => {
    const posA = parsePositionFromFileName(a.label)
    const posB = parsePositionFromFileName(b.label)
    return posA - posB
  })

  return result
}

const uploadButtonConfig: Record<
  PhotoCategory,
  { modifier: BUTTON_MODIFIER; severity?: BUTTON_SEVERITY }
> = {
  before: {
    modifier: BUTTON_MODIFIER.PRIMARY,
  },
  after: {
    modifier: BUTTON_MODIFIER.PRIMARY,
    severity: BUTTON_SEVERITY.SUCCESS_FILLED,
  },
}

interface SortablePhotoCardProps {
  id: string
  photo: PhotoData
  onDoubleClick: () => void
  onClick: () => void
}

const SortablePhotoCard: React.FC<SortablePhotoCardProps> = ({
  id,
  photo,
  onDoubleClick,
  onClick,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    animateLayoutChanges: () => false, // Disable layout animations for smoother control
  })

  const dragTransform = CSS.Transform.toString(transform)

  const style: React.CSSProperties = {}

  if (isDragging) {
    // Hide the dragged photo (DragOverlay shows it)
    style.opacity = 0
    style.transition = 'opacity 0.2s ease'
  } else if (dragTransform) {
    // Other photos moving into position - smooth transition during drag
    style.transform = dragTransform
    style.transition = transition || 'transform 0.2s ease'
  } else {
    // Normal state
    style.opacity = 1
    style.transition = 'opacity 0.2s ease'
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.photoCard}
      title='Double-click to edit'
      onDoubleClick={onDoubleClick}
      onClick={onClick}
      {...attributes}
      {...listeners}>
      <img src={photo.imageUrl} alt={photo.label} className={styles.photoImage} draggable={false} />
    </div>
  )
}

export type PhotosProps = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  disableActions?: boolean
  className?: string
  autoHeight?: boolean
  onRegisterLeaveAction?: ((handler: (() => Promise<void>) | null) => void) | undefined
}

export const Photos = ({
  selectedPatientId,
  selectedEncounterUid,
  disableActions = false,
  className,
  autoHeight = false,
  onRegisterLeaveAction,
}: PhotosProps) => {
  const dispatch = useAppDispatch()
  const { setNotification } = useNotification()
  const { siteId } = useParams<{ siteId: string }>()
  const [photos, setPhotos] = useState<Record<PhotoCategory, PhotoData[]>>(createEmptyPhotoColumns)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalCategory, setModalCategory] = useState<PhotoCategory | null>(null)
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null)
  const [isRetakeMode, setIsRetakeMode] = useState(false)
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [categoryUploadState, setCategoryUploadState] = useState<
    Record<PhotoCategory, CategoryUploadState>
  >({
    before: {
      isUploading: false,
      fileCount: 0,
    },
    after: {
      isUploading: false,
      fileCount: 0,
    },
  })
  const [modalUploadState, setModalUploadState] = useState<ModalUploadState>({
    isUploading: false,
    fileCount: 0,
    category: null,
  })
  const [previewPhotoId, setPreviewPhotoId] = useState<number | null>(null)
  const [previewPhotoCategory, setPreviewPhotoCategory] = useState<PhotoCategory | null>(null)
  const [photoNoteDrafts, setPhotoNoteDrafts] = useState<Record<string, string>>({})
  const [photoNoteAutosaveStatuses, setPhotoNoteAutosaveStatuses] = useState<
    Record<string, PhotoNoteAutosaveStatus>
  >({})
  const [photoNoteLastSavedAt, setPhotoNoteLastSavedAt] = useState<Record<string, string>>({})
  const photoNoteAutosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPhotoNoteAutosavingRef = useRef(false)
  const rerunPhotoNoteAutosaveRef = useRef(false)
  const [isEncounterTransitionLoading, setIsEncounterTransitionLoading] = useState(false)
  const encounterTransitionFulfilledTimeRef = useRef<number | undefined>(undefined)
  const galleryUploadPromiseRef = useRef<Promise<unknown> | null>(null)
  const modalUploadPromiseRef = useRef<Promise<unknown> | null>(null)
  const galleryInputRefs = useRef<Record<PhotoCategory, HTMLInputElement | null>>({
    before: null,
    after: null,
  })
  const encounterPhotosErrorKeyRef = useRef<string | null>(null)
  const uploadEncounterPhotoFiles = useEncounterPhotoUpload()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
  )

  const shouldSkipPhotosQuery = !siteId || !selectedPatientId || !selectedEncounterUid
  const encounterKey =
    selectedPatientId && selectedEncounterUid
      ? `${selectedPatientId}:${selectedEncounterUid}`
      : null
  const buildPhotoUploadContext = (category: PhotoCategory) => ({
    category,
    patientId: selectedPatientId,
    encounterUid: selectedEncounterUid,
    siteId,
  })

  const registerModalUploadTask = useCallback(
    (task: Promise<unknown> | null, context?: ModalUploadTaskContext) => {
      if (!task) {
        modalUploadPromiseRef.current = null
        setModalUploadState({
          isUploading: false,
          fileCount: 0,
          category: null,
        })
        return
      }

      modalUploadPromiseRef.current = task
      setModalUploadState({
        isUploading: true,
        fileCount: context?.fileCount ?? 0,
        category: context?.category ?? null,
      })

      void task.finally(() => {
        if (modalUploadPromiseRef.current === task) {
          modalUploadPromiseRef.current = null
          setModalUploadState({
            isUploading: false,
            fileCount: 0,
            category: null,
          })
        }
      })
    },
    [],
  )

  const {
    currentData: encounterPhotos,
    isFetching: isFetchingPhotos,
    isLoading: isLoadingPhotos,
    isError: isEncounterPhotosError,
    error: encounterPhotosError,
    fulfilledTimeStamp,
    refetch: refetchEncounterPhotos,
  } = useFetchEncounterPhotosQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedEncounterUid!,
    },
    {
      skip: shouldSkipPhotosQuery,
      refetchOnMountOrArgChange: true,
    },
  )

  const [updateEncounterPhotos] = useUpdateEncounterPhotosMutation()

  const waitForInFlightPhotoUploads = useCallback(async () => {
    const activeUploads = [galleryUploadPromiseRef.current, modalUploadPromiseRef.current].filter(
      (upload): upload is Promise<unknown> => Boolean(upload),
    )

    if (activeUploads.length === 0) {
      return
    }

    await Promise.all(activeUploads)
  }, [])

  const snapshotGalleryFiles = useCallback(
    async (
      category: PhotoCategory,
      selectedFiles: File[],
      selectedFilesData: {
        source: string
        selectedFilesCount: number
        fileNames: string[]
        fileSizes: number[]
      },
    ): Promise<{
      snapshottedFiles: File[]
      snapshotFailures: EncounterPhotoUploadFailure[]
    }> => {
      addPhotoUploadBreadcrumb('Gallery file snapshot started (parallel)', {
        ...selectedFilesData,
        category,
      })

      const results = await Promise.allSettled(
        selectedFiles.map((file) => snapshotEncounterPhotoFile(file)),
      )

      const snapshottedFiles: File[] = []
      const snapshotFailures: EncounterPhotoUploadFailure[] = []

      results.forEach((result, order) => {
        const file = selectedFiles[order]

        if (result.status === 'fulfilled') {
          snapshottedFiles.push(result.value)
          addPhotoUploadBreadcrumb('Gallery file snapshotted', {
            category,
            order,
            fileName: file.name,
            fileSize: file.size,
          })
          return
        }

        const errorDescription = getEncounterPhotoErrorDescription(
          result.reason,
          getEncounterPhotoSelectionReadErrorMessage(),
        )

        addPhotoUploadBreadcrumb(
          'Gallery file snapshot failed (all strategies exhausted)',
          {
            category,
            order,
            fileName: file.name,
            fileSize: file.size,
            error: Array.isArray(errorDescription) ? errorDescription.join(' ') : errorDescription,
          },
          'error',
        )

        snapshotFailures.push({
          order,
          fileName: file.name,
          sourceFileName: file.name,
          description: errorDescription,
        })
      })

      addPhotoUploadBreadcrumb('Gallery file snapshot completed', {
        ...selectedFilesData,
        category,
        succeededCount: snapshottedFiles.length,
        failedCount: snapshotFailures.length,
      })

      return { snapshottedFiles, snapshotFailures }
    },
    [],
  )

  const clearPhotoNoteAutosaveTimer = useCallback(() => {
    if (photoNoteAutosaveTimeoutRef.current) {
      clearTimeout(photoNoteAutosaveTimeoutRef.current)
      photoNoteAutosaveTimeoutRef.current = null
    }
  }, [])

  useLayoutEffect(() => {
    clearPhotoNoteAutosaveTimer()
    setPhotos(createEmptyPhotoColumns())
    setIsModalOpen(false)
    setModalCategory(null)
    setEditingPhotoId(null)
    setIsRetakeMode(false)
    setIsSaving(false)
    setCategoryUploadState({
      before: {
        isUploading: false,
        fileCount: 0,
      },
      after: {
        isUploading: false,
        fileCount: 0,
      },
    })
    setModalUploadState({
      isUploading: false,
      fileCount: 0,
      category: null,
    })
    setPhotoNoteDrafts({})
    setPhotoNoteAutosaveStatuses({})
    setPhotoNoteLastSavedAt({})
    setPreviewPhotoId(null)
    setPreviewPhotoCategory(null)
    setActiveId(null)
    setIsEncounterTransitionLoading(!shouldSkipPhotosQuery)
    galleryUploadPromiseRef.current = null
    modalUploadPromiseRef.current = null
    encounterPhotosErrorKeyRef.current = null
    encounterTransitionFulfilledTimeRef.current = fulfilledTimeStamp
  }, [clearPhotoNoteAutosaveTimer, encounterKey, shouldSkipPhotosQuery])

  useEffect(() => {
    return () => {
      dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.EncounterPhotos]))
    }
  }, [dispatch])

  useEffect(() => {
    return () => {
      clearPhotoNoteAutosaveTimer()
    }
  }, [clearPhotoNoteAutosaveTimer])

  useEffect(() => {
    if (shouldSkipPhotosQuery) {
      setIsEncounterTransitionLoading(false)
      setPhotos(createEmptyPhotoColumns())
      return
    }

    if (isEncounterTransitionLoading) {
      const hasFreshEncounterResponse =
        typeof fulfilledTimeStamp === 'number' &&
        fulfilledTimeStamp !== encounterTransitionFulfilledTimeRef.current
      const hasEncounterRequestFailed =
        isEncounterPhotosError && !isFetchingPhotos && !isLoadingPhotos

      if (!hasFreshEncounterResponse && !hasEncounterRequestFailed) {
        return
      }

      if (encounterPhotos && encounterPhotos.length) {
        setPhotos(mapApiPhotosToColumns(encounterPhotos))
      } else {
        setPhotos(createEmptyPhotoColumns())
      }

      setIsEncounterTransitionLoading(false)
      return
    }

    if (encounterPhotos && encounterPhotos.length) {
      setPhotos(mapApiPhotosToColumns(encounterPhotos))
    } else if (!isFetchingPhotos && !shouldSkipPhotosQuery) {
      setPhotos(createEmptyPhotoColumns())
    }
  }, [
    encounterPhotos,
    fulfilledTimeStamp,
    isEncounterTransitionLoading,
    isEncounterPhotosError,
    isFetchingPhotos,
    isLoadingPhotos,
    shouldSkipPhotosQuery,
  ])

  useEffect(() => {
    if (shouldSkipPhotosQuery) {
      encounterPhotosErrorKeyRef.current = null
      return
    }

    if (isEncounterPhotosError && !isFetchingPhotos && !isLoadingPhotos) {
      const nextErrorKey = `${encounterKey}:load`

      if (encounterPhotosErrorKeyRef.current === nextErrorKey) {
        return
      }

      encounterPhotosErrorKeyRef.current = nextErrorKey
      showPersistentPhotoError(
        setNotification,
        'Photos failed to load',
        encounterPhotosError,
        'Unable to load encounter photos.',
      )
      return
    }

    encounterPhotosErrorKeyRef.current = null
  }, [
    encounterKey,
    encounterPhotosError,
    isEncounterPhotosError,
    isFetchingPhotos,
    isLoadingPhotos,
    setNotification,
    shouldSkipPhotosQuery,
  ])

  const handleOpenModal = (
    category: PhotoCategory,
    photoId: string | null = null,
    isRetake = false,
  ) => {
    if (galleryUploadPromiseRef.current || modalUploadPromiseRef.current) {
      addPhotoUploadBreadcrumb('Photo dialog blocked: upload already in progress', {
        source: 'photos',
        category,
        photoId,
        isRetake,
      })
      return
    }

    setModalCategory(category)
    setEditingPhotoId(photoId)
    setIsRetakeMode(isRetake)
    setIsModalOpen(true)
  }

  const handleModalSubmit = async (
    photoData: PhotoData | PhotoData[],
    options?: {
      closeModal?: boolean
    },
  ) => {
    if (!modalCategory) return

    if (options?.closeModal !== false) {
      setIsModalOpen(false)
      setEditingPhotoId(null)
      setModalCategory(null)
      setIsRetakeMode(false)
    }

    if (editingPhotoId) {
      const singlePhoto = Array.isArray(photoData) ? photoData[0] : photoData
      setPhotos((prev) => {
        const updated = { ...prev }
        const column = [...updated[modalCategory]]
        const index = column.findIndex((photo) => photo.id === editingPhotoId)
        if (index > -1) {
          column[index] = isRetakeMode
            ? { ...column[index], isPending: true, imageUrl: '' }
            : { ...column[index], ...singlePhoto }
        }
        updated[modalCategory] = column
        return updated
      })

      if (isRetakeMode) {
        const oldPhotoId = parseInt(editingPhotoId, 10)
        const retakeCleanupContext = {
          category: modalCategory,
          patientId: selectedPatientId,
          encounterUid: selectedEncounterUid,
          siteId,
          photoId: !isNaN(oldPhotoId) ? oldPhotoId : editingPhotoId,
          isRetake: true,
          photoFileName: editingPhoto?.label ?? singlePhoto.label ?? null,
        }

        if (isNaN(oldPhotoId) || !siteId || !selectedPatientId || !selectedEncounterUid) {
          capturePhotoActionMessage(
            'Retake cleanup skipped: missing required fields',
            retakeCleanupContext,
            {
              source: 'photos',
              missingFields: [
                isNaN(oldPhotoId) && 'photoId',
                !siteId && 'siteId',
                !selectedPatientId && 'selectedPatientId',
                !selectedEncounterUid && 'selectedEncounterUid',
              ].filter(Boolean),
              newPhotoId: singlePhoto.id,
            },
            'warning',
          )
        } else {
          addPhotoActionBreadcrumb(
            'Retake cleanup started: deleting old photo',
            retakeCleanupContext,
            {
              source: 'photos',
              newPhotoId: singlePhoto.id,
              newPhotoLabel: singlePhoto.label,
            },
          )
          try {
            await retryEncounterPhotoUpdates({
              execute: (files) =>
                updateEncounterPhotos({
                  siteId,
                  patientId: selectedPatientId,
                  encounterUid: selectedEncounterUid,
                  files,
                }).unwrap(),
              files: [{ file_id: oldPhotoId, delete: 1 }],
              fallbackMessage: 'Failed to delete old photo during retake.',
            })
            capturePhotoActionMessage(
              'Retake cleanup completed: old photo deleted',
              retakeCleanupContext,
              {
                source: 'photos',
                newPhotoId: singlePhoto.id,
                newPhotoLabel: singlePhoto.label,
              },
            )
          } catch (error) {
            capturePhotoActionError(error, retakeCleanupContext, {
              source: 'photos',
              phase: 'retake-cleanup-delete-old-photo',
              newPhotoId: singlePhoto.id,
            })
            showPersistentPhotoError(
              setNotification,
              'Delete failed',
              error,
              'Failed to delete old photo.',
            )
          }
          void refetchEncounterPhotos()
        }
      }
    }

    if (!editingPhotoId && !shouldSkipPhotosQuery) {
      void refetchEncounterPhotos()
    }
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
    setEditingPhotoId(null)
    setModalCategory(null)
    setIsRetakeMode(false)
  }

  const handleOpenGalleryPicker = (category: PhotoCategory) => {
    if (galleryUploadPromiseRef.current || modalUploadPromiseRef.current) {
      addPhotoUploadBreadcrumb('Gallery picker ignored: upload already in progress', {
        source: 'photos',
        category,
      })
      return
    }

    if (!siteId || !selectedPatientId || !selectedEncounterUid) {
      capturePhotoUploadWarning(
        'Gallery picker blocked: missing required fields',
        buildPhotoUploadContext(category),
        {
          source: 'photos',
          missingFields: [
            !siteId && 'siteId',
            !selectedPatientId && 'selectedPatientId',
            !selectedEncounterUid && 'selectedEncounterUid',
          ].filter(Boolean),
        },
      )
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Upload failed',
        description: 'Missing required information to upload photo.',
      })
      return
    }

    addPhotoUploadBreadcrumb('Gallery picker opened', {
      source: 'photos',
      category,
      patientId: selectedPatientId,
      encounterUid: selectedEncounterUid,
      siteId,
    })
    galleryInputRefs.current[category]?.click()
  }

  const handleGalleryUpload = async (
    category: PhotoCategory,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files || [])
    // Keep a reference to the input element so we can clear it AFTER snapshot.
    // Clearing the input immediately (event.target.value = '') can cause Chrome
    // Android to revoke content:// URI permissions before async reads complete.
    const fileInputElement = event.target
    const sentryContext = buildPhotoUploadContext(category)
    const selectedFilesData = {
      source: 'photos',
      selectedFilesCount: selectedFiles.length,
      fileNames: selectedFiles.map((file) => file.name),
      fileSizes: selectedFiles.map((file) => file.size),
    }

    if (selectedFiles.length === 0) {
      fileInputElement.value = ''
      addPhotoUploadBreadcrumb('Gallery upload canceled: no files selected', {
        ...selectedFilesData,
        category,
        patientId: selectedPatientId,
        encounterUid: selectedEncounterUid,
        siteId,
      })
      return
    }

    if (!siteId || !selectedPatientId || !selectedEncounterUid) {
      fileInputElement.value = ''
      capturePhotoUploadWarning('Gallery upload aborted: missing required fields', sentryContext, {
        ...selectedFilesData,
        missingFields: [
          !siteId && 'siteId',
          !selectedPatientId && 'selectedPatientId',
          !selectedEncounterUid && 'selectedEncounterUid',
        ].filter(Boolean),
      })
      return
    }

    if (galleryUploadPromiseRef.current || modalUploadPromiseRef.current) {
      fileInputElement.value = ''
      capturePhotoUploadWarning(
        'Gallery upload blocked: another upload is already in progress',
        sentryContext,
        {
          ...selectedFilesData,
        },
      )
      return
    }

    capturePhotoUploadWarning('Gallery upload started', sentryContext, selectedFilesData)

    // Start snapshotting immediately — reads all files in parallel to minimize
    // the window where Android can invalidate content:// URI file handles.
    const snapshotPromise = snapshotGalleryFiles(category, selectedFiles, selectedFilesData)

    setCategoryUploadState((prevState) => ({
      ...prevState,
      [category]: {
        isUploading: true,
        fileCount: selectedFiles.length,
      },
    }))

    let hasShownUploadError = false

    // Set the upload lock BEFORE the IIFE starts executing to prevent
    // a rapid second call from passing the guard while the first await yields.
    // We use a sentinel initially, then replace with the real promise below.
    galleryUploadPromiseRef.current = Promise.resolve()

    const uploadTask = (async () => {
      try {
        const { snapshottedFiles, snapshotFailures } = await snapshotPromise

        // Now that bytes are safely in memory, clear the file input so the same
        // files can be re-selected. This MUST happen after snapshot — clearing
        // earlier causes Chrome Android to revoke content:// URI permissions.
        fileInputElement.value = ''

        if (snapshottedFiles.length === 0) {
          const allFailedError = createEncounterPhotoUploadFailureError(
            snapshotFailures,
            'All selected photos could not be read. Please try selecting them again, or use the Files app instead of Gallery/Photos.',
          )

          capturePhotoUploadError(allFailedError, sentryContext, {
            ...selectedFilesData,
            phase: 'snapshot-all-failed',
            snapshotFailureCount: snapshotFailures.length,
          })
          showPersistentPhotoError(
            setNotification,
            `${columnLabels[category]} gallery upload failed`,
            allFailedError,
            'All selected photos could not be read.',
          )
          hasShownUploadError = true
          throw allFailedError
        }

        const { uploadedPhotos, failures: uploadFailures } = await uploadEncounterPhotoFiles({
          category,
          files: snapshottedFiles,
          existingPhotos: encounterPhotos || [],
          siteId,
          patientId: selectedPatientId,
          encounterUid: selectedEncounterUid,
        })

        const allFailures = [...snapshotFailures, ...uploadFailures]

        capturePhotoUploadWarning('Gallery upload completed', sentryContext, {
          ...selectedFilesData,
          uploadedCount: uploadedPhotos.length,
          snapshotFailureCount: snapshotFailures.length,
          uploadFailureCount: uploadFailures.length,
          totalFailureCount: allFailures.length,
        })

        if (uploadedPhotos.length > 0 && !shouldSkipPhotosQuery) {
          dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.ESignForm]))
          addPhotoUploadBreadcrumb('Gallery upload succeeded: refreshing encounter photos', {
            ...selectedFilesData,
            category,
            uploadedCount: uploadedPhotos.length,
            totalFailureCount: allFailures.length,
          })
          void refetchEncounterPhotos()
        }

        if (allFailures.length > 0) {
          const partialFailureError = createEncounterPhotoUploadFailureError(
            allFailures,
            'Failed to upload photo(s).',
          )

          capturePhotoUploadWarning('Gallery upload partial failure', sentryContext, {
            ...selectedFilesData,
            uploadedCount: uploadedPhotos.length,
            snapshotFailureCount: snapshotFailures.length,
            uploadFailureCount: uploadFailures.length,
            failures: allFailures.map((failure) => ({
              order: failure.order,
              fileName: failure.sourceFileName,
              description: failure.description,
            })),
          })
          showPersistentPhotoError(
            setNotification,
            `${columnLabels[category]} gallery upload failed`,
            partialFailureError,
            'Failed to upload photo(s).',
          )
          hasShownUploadError = true
          throw partialFailureError
        }
      } catch (error) {
        capturePhotoUploadError(error, sentryContext, {
          ...selectedFilesData,
          phase: 'handleGalleryUpload',
        })

        if (!hasShownUploadError) {
          showPersistentPhotoError(
            setNotification,
            `${columnLabels[category]} gallery upload failed`,
            error,
            'Failed to upload photo(s).',
          )
        }

        throw error
      } finally {
        // Safety net: ensure input is always cleared even if snapshot throws
        // before reaching the post-snapshot clear above.
        fileInputElement.value = ''

        setCategoryUploadState((prevState) => ({
          ...prevState,
          [category]: {
            isUploading: false,
            fileCount: 0,
          },
        }))

        if (galleryUploadPromiseRef.current === uploadTask) {
          galleryUploadPromiseRef.current = null
        }
      }
    })()

    galleryUploadPromiseRef.current = uploadTask

    try {
      await uploadTask
    } catch {
      // Error was already surfaced to the user and must stay rejected for leave-action waits.
    }
  }

  const persistPhotoNoteDrafts = useCallback(async () => {
    if (disableActions) {
      return
    }

    if (isPhotoNoteAutosavingRef.current) {
      rerunPhotoNoteAutosaveRef.current = true
      return
    }

    if (!siteId || !selectedPatientId || !selectedEncounterUid) {
      return
    }

    const draftEntries = Object.entries(photoNoteDrafts)
    if (draftEntries.length === 0) {
      return
    }

    const photoById = new Map<string, PhotoData>()
    photos.before.forEach((photo) => photoById.set(photo.id, photo))
    photos.after.forEach((photo) => photoById.set(photo.id, photo))

    const pendingUpdates = draftEntries.flatMap(([photoId, draftValue]) => {
      const photo = photoById.get(photoId)
      if (!photo) {
        return []
      }

      const fileId = parseInt(photo.id, 10)
      if (isNaN(fileId)) {
        return []
      }

      const currentNote = photo.note ?? ''
      if (currentNote === draftValue) {
        return []
      }

      return [{ photoId, fileId, fileName: photo.label, note: draftValue }]
    })

    if (pendingUpdates.length === 0) {
      setPhotoNoteDrafts((prevDrafts) => {
        const nextDrafts = { ...prevDrafts }

        Object.entries(prevDrafts).forEach(([photoId, draftValue]) => {
          const photo = photoById.get(photoId)
          const currentNote = photo?.note ?? ''
          if (!photo || currentNote === draftValue) {
            delete nextDrafts[photoId]
          }
        })

        return nextDrafts
      })
      return
    }

    const savingPhotoIds = pendingUpdates.map((item) => item.photoId)
    setPhotoNoteAutosaveStatuses((prevStatuses) => {
      const nextStatuses = { ...prevStatuses }
      savingPhotoIds.forEach((photoId) => {
        nextStatuses[photoId] = 'saving'
      })
      return nextStatuses
    })

    isPhotoNoteAutosavingRef.current = true

    try {
      const saveSnapshot = pendingUpdates.reduce<Record<string, string>>((acc, item) => {
        acc[item.photoId] = item.note
        return acc
      }, {})

      await retryEncounterPhotoUpdates({
        execute: (files) =>
          updateEncounterPhotos({
            siteId,
            patientId: selectedPatientId,
            encounterUid: selectedEncounterUid,
            files,
          }).unwrap(),
        files: pendingUpdates.map((item) => ({
          file_id: item.fileId,
          file_name: item.fileName,
          note: item.note,
        })),
        fallbackMessage: 'Unable to autosave photo note.',
      })

      const savedAt = DateService.formatDisplayDate(
        new Date().toISOString(),
        true,
        false,
        false,
      ).trim()

      setPhotos((prevPhotos) => {
        const applyNoteSnapshot = (column: PhotoData[]) =>
          column.map((photo) =>
            saveSnapshot[photo.id] === undefined
              ? photo
              : { ...photo, note: saveSnapshot[photo.id] },
          )

        return {
          before: applyNoteSnapshot(prevPhotos.before),
          after: applyNoteSnapshot(prevPhotos.after),
        }
      })

      setPhotoNoteDrafts((prevDrafts) => {
        const nextDrafts = { ...prevDrafts }

        Object.entries(saveSnapshot).forEach(([photoId, savedNote]) => {
          if (nextDrafts[photoId] === savedNote) {
            delete nextDrafts[photoId]
          }
        })

        return nextDrafts
      })

      setPhotoNoteAutosaveStatuses((prevStatuses) => {
        const nextStatuses = { ...prevStatuses }
        Object.keys(saveSnapshot).forEach((photoId) => {
          nextStatuses[photoId] = 'saved'
        })
        return nextStatuses
      })

      setPhotoNoteLastSavedAt((prevSavedAt) => {
        const nextSavedAt = { ...prevSavedAt }
        Object.keys(saveSnapshot).forEach((photoId) => {
          nextSavedAt[photoId] = savedAt
        })
        return nextSavedAt
      })
    } catch (error) {
      setPhotoNoteAutosaveStatuses((prevStatuses) => {
        const nextStatuses = { ...prevStatuses }
        savingPhotoIds.forEach((photoId) => {
          nextStatuses[photoId] = 'error'
        })
        return nextStatuses
      })

      showPersistentPhotoError(
        setNotification,
        'Autosave failed',
        error,
        'Unable to autosave photo note.',
      )
    } finally {
      isPhotoNoteAutosavingRef.current = false

      if (rerunPhotoNoteAutosaveRef.current) {
        rerunPhotoNoteAutosaveRef.current = false
        void persistPhotoNoteDrafts()
      }
    }
  }, [
    disableActions,
    photoNoteDrafts,
    photos.after,
    photos.before,
    selectedEncounterUid,
    selectedPatientId,
    setNotification,
    siteId,
    updateEncounterPhotos,
  ])

  const flushAutosaveNow = useCallback(async () => {
    clearPhotoNoteAutosaveTimer()
    await persistPhotoNoteDrafts()
  }, [clearPhotoNoteAutosaveTimer, persistPhotoNoteDrafts])

  const handleLeaveAction = useCallback(async () => {
    await flushAutosaveNow()
    await waitForInFlightPhotoUploads()
  }, [flushAutosaveNow, waitForInFlightPhotoUploads])

  useEffect(() => {
    clearPhotoNoteAutosaveTimer()

    if (
      disableActions ||
      !siteId ||
      !selectedPatientId ||
      !selectedEncounterUid ||
      Object.keys(photoNoteDrafts).length === 0
    ) {
      return
    }

    photoNoteAutosaveTimeoutRef.current = setTimeout(() => {
      void persistPhotoNoteDrafts()
    }, PHOTO_NOTE_AUTOSAVE_DELAY_MS)

    return clearPhotoNoteAutosaveTimer
  }, [
    clearPhotoNoteAutosaveTimer,
    disableActions,
    persistPhotoNoteDrafts,
    photoNoteDrafts,
    selectedEncounterUid,
    selectedPatientId,
    siteId,
  ])

  useEffect(() => {
    if (!onRegisterLeaveAction) {
      return
    }

    onRegisterLeaveAction(handleLeaveAction)

    return () => {
      onRegisterLeaveAction(null)
    }
  }, [handleLeaveAction, onRegisterLeaveAction])

  const handlePhotoNoteChange = (photoId: string, value: string) => {
    setPhotoNoteDrafts((prevDrafts) => ({
      ...prevDrafts,
      [photoId]: value,
    }))
    setPhotoNoteAutosaveStatuses((prevStatuses) => ({
      ...prevStatuses,
      [photoId]: 'idle',
    }))
    setPhotoNoteLastSavedAt((prevSavedAt) => {
      if (!Object.prototype.hasOwnProperty.call(prevSavedAt, photoId)) {
        return prevSavedAt
      }

      const nextSavedAt = { ...prevSavedAt }
      delete nextSavedAt[photoId]
      return nextSavedAt
    })
  }

  const getPhotoNoteAutosaveLabel = (photoId: string) => {
    const status = photoNoteAutosaveStatuses[photoId] || 'idle'
    const lastSavedAt = photoNoteLastSavedAt[photoId]

    if (status === 'saving') {
      return 'Saving...'
    }

    if (status === 'saved' && lastSavedAt) {
      return `Last autosaved at ${lastSavedAt}`
    }

    if (status === 'error') {
      return 'Autosave failed'
    }

    return 'Input is autosaving'
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (
      isSaving ||
      !over ||
      !siteId ||
      !selectedPatientId ||
      !selectedEncounterUid ||
      !encounterPhotos
    ) {
      return
    }

    const activeIdStr = active.id.toString()
    const overIdStr = over.id.toString()

    const parseId = (id: string) => {
      const parts = id.split('-')
      if (parts.length >= 2) {
        const category = parts[0] as PhotoCategory
        const index = parseInt(parts[1], 10)
        return { category, index }
      }
      return null
    }

    const activeData = parseId(activeIdStr)
    const overData = parseId(overIdStr)

    if (!activeData || !overData || activeData.category !== overData.category) {
      return
    }

    const { category, index: activeIndex } = activeData
    const { index: overIndex } = overData

    if (activeIndex === overIndex) {
      return
    }

    const currentColumn = [...photos[category]]
    const [moved] = currentColumn.splice(activeIndex, 1)
    const reorderedPhotos = [...currentColumn]
    reorderedPhotos.splice(overIndex, 0, moved)

    setPhotos((prev) => {
      const updated = { ...prev }
      updated[category] = reorderedPhotos
      return updated
    })

    setIsSaving(true)

    const photoIdToOriginalPhoto = new Map<number, EncounterPhotoApiItem>()
    encounterPhotos.forEach((photo) => {
      const photoCategory = isPhotoCategory(photo.category)
        ? photo.category
        : parseCategoryFromFileName(photo.filename)
      if (photoCategory === category) {
        photoIdToOriginalPhoto.set(photo.id, photo)
      }
    })

    const filesToUpdate = reorderedPhotos
      .map((photo, index) => {
        const photoId = parseInt(photo.id, 10)
        if (isNaN(photoId)) return null

        const originalPhoto = photoIdToOriginalPhoto.get(photoId)
        if (!originalPhoto || !originalPhoto.filename) return null

        const newPosition = index + 1
        const newFileName = updatePositionInFileName(originalPhoto.filename, newPosition)

        if (newFileName !== originalPhoto.filename) {
          return {
            file_id: photoId,
            file_name: newFileName,
          }
        }
        return null
      })
      .filter((file): file is { file_id: number; file_name: string } => file !== null)

    try {
      if (filesToUpdate.length > 0) {
        await retryEncounterPhotoUpdates({
          execute: (files) =>
            updateEncounterPhotos({
              siteId,
              patientId: selectedPatientId,
              encounterUid: selectedEncounterUid,
              files,
            }).unwrap(),
          files: filesToUpdate,
          fallbackMessage: 'Failed to reorder photos.',
        })
      }
    } catch (error) {
      showPersistentPhotoError(
        setNotification,
        'Photo reorder failed',
        error,
        'Failed to reorder photos.',
      )
      void refetchEncounterPhotos()
    } finally {
      setIsSaving(false)
    }
  }

  const editingPhoto =
    modalCategory && editingPhotoId
      ? photos[modalCategory].find((photo) => photo.id === editingPhotoId)
      : null

  const renderPhotoSkeleton = () => (
    <div className={classNames(styles.photoCard, styles.photoCardSkeleton)}>
      <Skeleton.Image active style={{ width: '100%', height: '100%' }} />
    </div>
  )

  const renderPhotoCell = (
    photo: PhotoData | null,
    category: PhotoCategory,
    index: number,
    isSkeleton: boolean = false,
  ) => {
    const positionNumber = index + 1

    if (isSkeleton) {
      return (
        <div key={`skeleton-${category}-${index}`} className={styles.photoCell}>
          <div className={styles.photoCellHeader}>
            <div className={styles.positionLabel}>#{positionNumber}</div>
          </div>
          <div className={styles.photoMedia}>{renderPhotoSkeleton()}</div>
        </div>
      )
    }

    if (!photo) {
      return <></>
    }

    if (photo.isPending) {
      return (
        <div key={photo.id} className={styles.photoCell}>
          <div className={styles.photoCellHeader}>
            <div className={styles.positionLabel}>#{positionNumber}</div>
          </div>
          <div className={styles.photoMedia}>{renderPhotoSkeleton()}</div>
        </div>
      )
    }

    const id = `${category}-${index}`
    const noteValue = photoNoteDrafts[photo.id] ?? photo.note ?? ''
    const noteAutosaveStatus = photoNoteAutosaveStatuses[photo.id] || 'idle'
    const noteAutosaveLabel = getPhotoNoteAutosaveLabel(photo.id)

    return (
      <div key={photo.id} className={styles.photoCell}>
        <div className={styles.photoCellHeader}>
          <div className={styles.positionLabel}>#{positionNumber}</div>
        </div>
        <div className={styles.photoMedia}>
          <SortablePhotoCard
            id={id}
            photo={photo}
            onDoubleClick={() => !disableActions && handleOpenModal(category, photo.id)}
            onClick={() => {
              if (activeId) return
              const photoIdNum = parseInt(photo.id, 10)
              if (!isNaN(photoIdNum)) {
                setPreviewPhotoId(photoIdNum)
                setPreviewPhotoCategory(category)
              }
            }}
          />
        </div>
        <div className={styles.noteSection}>
          <TextArea
            autoSize={{ minRows: 3 }}
            value={noteValue}
            onChange={(event) => handlePhotoNoteChange(photo.id, event.target.value)}
            placeholder='Type here...'
            disabled={disableActions}
          />
          {!disableActions && noteAutosaveLabel && (
            <div className={styles.noteStatusRow}>
              <span
                className={classNames(styles.noteStatusLabel, {
                  [styles.noteStatusLabelSaving]: noteAutosaveStatus === 'saving',
                  [styles.noteStatusLabelSaved]:
                    noteAutosaveStatus === 'saved' && !!photoNoteLastSavedAt[photo.id],
                  [styles.noteStatusLabelError]: noteAutosaveStatus === 'error',
                })}>
                {noteAutosaveLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderRows = () => {
    const visiblePhotos = isEncounterTransitionLoading ? createEmptyPhotoColumns() : photos
    const beforePhotos = visiblePhotos.before
    const afterPhotos = visiblePhotos.after
    const isPhotosLoading = isEncounterTransitionLoading || isFetchingPhotos || isLoadingPhotos
    const loadingRowCount = isPhotosLoading ? 3 : 0
    const maxRows = Math.max(beforePhotos.length, afterPhotos.length, loadingRowCount)

    const beforeIds = beforePhotos.map((_, index) => `before-${index}`)
    const afterIds = afterPhotos.map((_, index) => `after-${index}`)
    const rowsCount = maxRows || 1

    const beforeColumn = Array.from({ length: rowsCount }).map((_, index) => {
      const photo = beforePhotos[index] || null
      const isSkeleton = isPhotosLoading && !photo
      const isLastRow = index === rowsCount - 1
      const isEmpty = !photo && !isSkeleton

      return (
        <div
          key={`before-row-${index}`}
          className={classNames(styles.photoCellWrapper, styles.photoCellWrapperBefore, {
            [styles.photoCellWrapperEmpty]: isEmpty,
            [styles.photoCellWrapperLastRow]: isLastRow,
          })}
          style={{ '--photo-grid-row': index + 1 } as React.CSSProperties}>
          {renderPhotoCell(photo, 'before', index, isSkeleton)}
        </div>
      )
    })

    const afterColumn = Array.from({ length: rowsCount }).map((_, index) => {
      const photo = afterPhotos[index] || null
      const isSkeleton = isPhotosLoading && !photo
      const isLastRow = index === rowsCount - 1
      const isEmpty = !photo && !isSkeleton

      return (
        <div
          key={`after-row-${index}`}
          className={classNames(styles.photoCellWrapper, styles.photoCellWrapperAfter, {
            [styles.photoCellWrapperEmpty]: isEmpty,
            [styles.photoCellWrapperLastRow]: isLastRow,
          })}
          style={{ '--photo-grid-row': index + 1 } as React.CSSProperties}>
          {renderPhotoCell(photo, 'after', index, isSkeleton)}
        </div>
      )
    })

    return (
      <div className={styles.tableGrid}>
        <SortableContext items={beforeIds} strategy={verticalListSortingStrategy}>
          {beforeColumn}
        </SortableContext>
        <SortableContext items={afterIds} strategy={verticalListSortingStrategy}>
          {afterColumn}
        </SortableContext>
      </div>
    )
  }

  const getActivePhoto = () => {
    if (!activeId) return null
    const idStr = activeId.toString()
    const parts = idStr.split('-')
    if (parts.length >= 2) {
      const category = parts[0] as PhotoCategory
      const index = parseInt(parts[1], 10)
      if (!isNaN(index) && photos[category] && photos[category][index]) {
        return photos[category][index]
      }
    }
    return null
  }

  const activePhoto = getActivePhoto()
  const activeGalleryUploadCategory = photoCategories.find(
    (category) => categoryUploadState[category].isUploading,
  )
  const activeGalleryUploadFileCount = activeGalleryUploadCategory
    ? categoryUploadState[activeGalleryUploadCategory].fileCount
    : 0
  const isAnyPhotoUploadInProgress =
    Boolean(activeGalleryUploadCategory) || modalUploadState.isUploading
  const galleryUploadOverlayLabel = activeGalleryUploadCategory
    ? `Uploading ${activeGalleryUploadFileCount || ''} ${columnLabels[
        activeGalleryUploadCategory
      ].toLowerCase()} photo${activeGalleryUploadFileCount === 1 ? '' : 's'}...`
    : ''

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      autoScroll={!disableActions && !isAnyPhotoUploadInProgress}>
      <div
        className={classNames(styles.container, className, {
          [styles.containerAuto]: autoHeight,
        })}>
        <div className={styles.actionsBar}>
          {!disableActions &&
            photoCategories.map((category) => (
              <div
                key={category}
                className={classNames(styles.actionGroup, {
                  [styles.actionGroupBefore]: category === 'before',
                  [styles.actionGroupAfter]: category === 'after',
                })}>
                <input
                  ref={(input) => {
                    galleryInputRefs.current[category] = input
                  }}
                  type='file'
                  accept='image/*'
                  multiple
                  className={styles.galleryInput}
                  disabled={disableActions || isAnyPhotoUploadInProgress}
                  onChange={(event) => void handleGalleryUpload(category, event)}
                />

                <Button
                  htmlType='button'
                  proportion={BUTTON_PROPORTION.SMALL}
                  icon={
                    category === 'before' ? <CameraIcon className={styles.uploadIcon} /> : undefined
                  }
                  modifier={uploadButtonConfig[category].modifier}
                  severity={uploadButtonConfig[category].severity}
                  className={classNames(styles.uploadButton, {
                    [styles.uploadButtonBefore]: category === 'before',
                    [styles.uploadButtonAfter]: category === 'after',
                  })}
                  disabled={disableActions || isAnyPhotoUploadInProgress}
                  onClick={() => handleOpenModal(category)}>
                  {category === 'after' ? (
                    <span className={styles.buttonContent}>
                      {uploadLabels[category]}
                      <CameraIcon className={styles.uploadIcon} />
                    </span>
                  ) : (
                    uploadLabels[category]
                  )}
                </Button>

                <Button
                  htmlType='button'
                  proportion={BUTTON_PROPORTION.SMALL}
                  modifier={BUTTON_MODIFIER.SECONDARY}
                  className={styles.galleryButton}
                  icon={<UploadIcon className={styles.galleryIcon} />}
                  title={galleryUploadLabels[category]}
                  aria-label={galleryUploadLabels[category]}
                  loading={categoryUploadState[category].isUploading}
                  disabled={disableActions || isAnyPhotoUploadInProgress}
                  onClick={() => handleOpenGalleryPicker(category)}
                />
              </div>
            ))}
        </div>

        <div className={classNames(styles.tableWrapper, { [styles.tableWrapperAuto]: autoHeight })}>
          {activeGalleryUploadCategory && (
            <div className={styles.galleryUploadOverlay}>
              <Spin size='large' tip={galleryUploadOverlayLabel} />
            </div>
          )}
          <div className={classNames(styles.tableBody, { [styles.tableBodyAuto]: autoHeight })}>
            {renderRows()}
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activePhoto ? (
            <div className={classNames(styles.photoCard, styles.dragOverlay)}>
              <img
                src={activePhoto.imageUrl}
                alt={activePhoto.label}
                className={styles.photoImage}
                draggable={false}
              />
            </div>
          ) : null}
        </DragOverlay>

        <ProcedurePictureModal
          open={isModalOpen}
          photo={isRetakeMode ? null : editingPhoto || null}
          category={modalCategory}
          existingPhotos={encounterPhotos || []}
          onCancel={handleModalCancel}
          onSubmit={handleModalSubmit}
          isRetake={isRetakeMode}
          retakeFileName={isRetakeMode ? editingPhoto?.label ?? null : null}
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          onUploadTaskChange={registerModalUploadTask}
        />

        <PhotoPreviewModal
          open={previewPhotoId !== null}
          photoId={previewPhotoId}
          category={previewPhotoCategory}
          onClose={() => {
            setPreviewPhotoId(null)
            setPreviewPhotoCategory(null)
          }}
          onDelete={() => {
            if (!shouldSkipPhotosQuery) {
              void refetchEncounterPhotos()
            }
          }}
          onRetake={() => {
            if (previewPhotoCategory) {
              handleOpenModal(
                previewPhotoCategory,
                previewPhotoId != null ? previewPhotoId.toString() : null,
                true,
              )
            }
          }}
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          readOnly={disableActions || isAnyPhotoUploadInProgress}
        />
      </div>
    </DndContext>
  )
}
